import React, { useEffect, useMemo, useState, useRef } from "react";
import AdminDashboard from "@/components/liquorit/AdminDashboard";
import { ArrowRight, LogOut, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import WaitlistDialog from "@/components/liquorit/WaitlistDialog";
import UserProfile from "@/components/liquorit/UserProfile";
import { useParallax, useRevealOnScroll } from "@/components/liquorit/scrollMotion";

// ─── Brand Constants ────────────────────────────────────────────────
const BRAND = {
  name: "Liquorit",
  tagline: "Mumbai's favourite bottle, delivered.",
  email: "hello@liquorit.in",
};

const NAV_LINKS = [
  { id: "how", label: "How It Works" },
  { id: "drivers", label: "Drive With Us" },
  { id: "faq", label: "FAQ" },
];

const HOW_IT_WORKS = [
  {
    icon: "wine_bar",
    title: "Browse the Selection",
    desc: "Spirits, wines, beers from verified stores near you. Filtered by what's available right now.",
  },
  {
    icon: "two_wheeler",
    title: "We Handle the Rest",
    desc: "A verified delivery partner picks and packs your order within minutes of confirmation.",
  },
  {
    icon: "door_front",
    title: "At Your Door",
    desc: "Watch your order move in real-time. No calls, no waiting, no guessing.",
  },
];

const FEATURES = [
  {
    icon: "verified",
    color: "gold",
    title: "Licensed Stores Only",
    desc: "100% authentic sourcing from Maharashtra-licensed retailers. No grey market.",
  },
  {
    icon: "location_on",
    color: "violet",
    title: "Live Order Tracking",
    desc: "Real-time GPS updates from store shelf to your doorstep. No black holes.",
  },
  {
    icon: "badge",
    color: "gold",
    title: "Age-Verified Delivery",
    desc: "Partners verify age at drop-off. Safe, compliant, accountable.",
  },
  {
    icon: "bolt",
    color: "bordeaux",
    title: "Fast. Genuinely Fast.",
    desc: "Routing built for Mumbai specifically. Not repurposed from groceries.",
  },
];

const DRIVER_PERKS = [
  {
    icon: "schedule",
    title: "Flexible Hours",
    desc: "Go online when you want. No minimum shift requirements.",
  },
  {
    icon: "payments",
    title: "Earn Per Delivery",
    desc: "Competitive base pay plus 100% of customer tips. Fast withdrawals.",
  },
  {
    icon: "map",
    title: "Mumbai Routes",
    desc: "Optimized zones in Bandra, Juhu, and South Bombay.",
  },
];

const MUMBAI_AREAS = [
  "Bandra West", "Juhu", "Colaba", "Powai", "Worli",
  "Andheri West", "Lower Parel", "Malad", "Borivali", "Thane",
];

const STORE_PARTNERS = [
  "Living Liquidz", "The Beer Cafe", "Nature's Basket",
  "Wine Park", "Liquid Library", "Juben Wines", "Chheda Stores",
  "Living Liquidz", "The Beer Cafe", "Nature's Basket",
  "Wine Park", "Liquid Library", "Juben Wines", "Chheda Stores",
];

const FAQS = [
  {
    q: "Is liquor delivery legal in Mumbai?",
    a: "Liquorit partners exclusively with Maharashtra-licensed retailers. Delivery is facilitated by licensed partners in compliance with state excise regulations. We verify age at every delivery.",
  },
  {
    q: "What's the minimum age to order?",
    a: "You must be 21 years or older to place an order or receive a delivery. Our partners verify age with a valid government ID at every drop-off, no exceptions.",
  },
  {
    q: "Which areas does Liquorit serve?",
    a: "We're currently launching in Mumbai — starting with Bandra, Juhu, Worli, Andheri, Lower Parel, and Powai. More zones are being added rapidly.",
  },
  {
    q: "How do I become a delivery partner?",
    a: "Simply apply to drive with us. Our onboarding team will personally reach out to verify your documents and get you set up."
  },
];

// ─── Utility ─────────────────────────────────────────────────────────────────
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function useScrollTopBg() {
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return solid;
}

// ─── Google Font Injector ─────────────────────────────────────────────────────
function usePremiumFonts() {
  useEffect(() => {
    if (document.getElementById("bottlerun-fonts")) return;
    const link = document.createElement("link");
    link.id = "bottlerun-fonts";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Inter:wght@300;400;500;600;700&family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap";
    document.head.appendChild(link);
  }, []);
}

// ─── Phone Mockup — Liquor App UI ────────────────────────────────────────────
function PhoneMock() {
  return (
    <div
      className="relative flex justify-center lg:justify-end items-center"
      style={{ height: 620 }}
    >
      {/* Amber radial glow behind phone */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 420, height: 420,
          background: "radial-gradient(circle, rgba(197,160,89,0.12) 0%, transparent 70%)",
          filter: "blur(40px)",
          right: -40, top: "50%", transform: "translateY(-50%)",
        }}
      />
      {/* Purple screen glow */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 280, height: 280,
          background: "radial-gradient(circle, rgba(124,59,237,0.18) 0%, transparent 70%)",
          filter: "blur(60px)",
          right: 10, top: "50%", transform: "translateY(-50%)", zIndex: 1,
        }}
      />

      {/* Phone body */}
      <div
        className="relative z-20"
        style={{
          width: 290, height: 580,
          borderRadius: 44,
          background: "#1C0A2E",
          border: "8px solid #2D1060",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(124,59,237,0.2)",
          overflow: "hidden",
          animation: "float 6s ease-in-out infinite",
        }}
      >
        {/* Notch */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 z-30"
          style={{ width: 100, height: 22, background: "#2D1060", borderRadius: "0 0 16px 16px" }}
        />

        {/* Map bg */}
        <div className="absolute inset-0">
          <img
            alt="Mumbai streets map"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuC3PrYV7LLn_DOTITj524lHBv6i1v-c7unxDTm-_76QNUG86x95Wz2QYXX5Ic7H_oFIJ1OKT_veyzmrg_SK4PDua8oEw611Sj2sx6sxy56F0dhhzRvRLAfO6FfiI30ACZeYNDxtlTzXvAAWGYjfAsL1k1mKhGUsjs5Fr13XcsnSKp2w3ey5Sxo8ZLiae6L1Y7Wa0scYoFD4HVU-iAylUnx7PSPeHtHuYFBgJtAa5yYm2LLNEARM9ryoWJQIY8nqMUHhO6pxfUUrq3zV"
            className="w-full h-full object-cover"
            style={{ opacity: 0.5, filter: "grayscale(0.4)" }}
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, #1C0A2E 30%, transparent 100%)" }}
          />
        </div>

        {/* Header bar */}
        <div
          className="absolute flex justify-between items-center px-5 z-10"
          style={{ top: 30, left: 0, right: 0 }}
        >
          <div style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(12px)", borderRadius: 999, padding: "6px 10px" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: "#fff" }}>menu</span>
          </div>
          <div
            className="flex items-center gap-1"
            style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 999, padding: "5px 12px" }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 12, color: "#C5A059" }}>location_on</span>
            <span style={{ fontSize: 11, color: "#fff" }}>Bandra West</span>
          </div>
        </div>

        {/* Product card */}
        <div
          className="absolute z-10"
          style={{ top: 90, left: 14, right: 14, background: "rgba(255,255,255,0.1)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 14 }}
        >
          <div className="flex items-start gap-3">
            <div style={{ width: 56, height: 70, background: "rgba(0,0,0,0.3)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 28, color: "#C5A059" }}>wine_bar</span>
            </div>
            <div>
              <p className="font-bold" style={{ fontSize: 13, color: "#fff" }}>Glenfiddich 12 Year</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginBottom: 8 }}>Single Malt Scotch</p>
              <div className="flex items-center gap-2">
                <span className="font-bold" style={{ fontSize: 13, color: "#C5A059" }}>₹5,800</span>
                <div style={{ background: "rgba(124,59,237,0.2)", borderRadius: 6, padding: "2px 8px", fontSize: 10, color: "#7C3AED", fontWeight: 700 }}>IN STOCK</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── IN-APP: Order Status toast (top-right of map) ── */}
        <div
          className="absolute z-20 flex items-center gap-2"
          style={{
            top: 210, right: 10,
            background: "rgba(8,5,8,0.88)",
            backdropFilter: "blur(14px)",
            border: "1px solid rgba(74,222,128,0.25)",
            borderRadius: 12,
            padding: "8px 10px",
            maxWidth: 148,
            // animation: "float 6.5s ease-in-out infinite",
          }}
        >
          <span className="material-symbols-outlined" style={{ color: "#4ade80", fontSize: 15, flexShrink: 0 }}>check_circle</span>
          <div>
            <p style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", lineHeight: 1 }}>Order Status</p>
            <p className="font-semibold" style={{ fontSize: 11, color: "#fff", marginTop: 2 }}>Out for Delivery</p>
          </div>
        </div>

        {/* ── IN-APP: Live Tracking toast (bottom-left of map) ── */}
        <div
          className="absolute z-20 flex items-center gap-2"
          style={{
            bottom: 84, left: 10,
            background: "rgba(8,5,8,0.88)",
            backdropFilter: "blur(14px)",
            border: "1px solid rgba(239,68,68,0.2)",
            borderRadius: 12,
            padding: "8px 10px",
            maxWidth: 155,
            // animation: "float 6.5s ease-in-out infinite",
          }}
        >
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#ef4444", animation: "pulse 1.5s ease-in-out infinite", flexShrink: 0 }} />
          <div>
            <p className="font-bold uppercase tracking-wider" style={{ fontSize: 8, color: "#fff", letterSpacing: "0.1em" }}>Live Tracking</p>
            <p style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", lineHeight: 1.3, marginTop: 1 }}>Rohan is 2km away</p>
          </div>
        </div>

        {/* Arriving pill */}
        <div
          className="absolute z-10 flex items-center gap-2"
          style={{ top: "52%", left: "50%", transform: "translate(-50%, -50%)", background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)", border: "1px solid rgba(197,160,89,0.3)", borderRadius: 999, padding: "8px 16px", whiteSpace: "nowrap" }}
        >
          <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#7C3AED", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 12, color: "#fff" }}>sports_motorsports</span>
          </div>
          <div>
            <p style={{ fontSize: 9, color: "rgba(255,255,255,0.55)" }}>Arriving in</p>
            <p className="font-bold" style={{ fontSize: 12, color: "#fff" }}>12 mins</p>
          </div>
        </div>

        {/* Bottom nav */}
        <div
          className="absolute bottom-0 left-0 right-0 flex justify-around items-center px-2"
          style={{ height: 70, background: "rgba(28,10,46,0.92)", backdropFilter: "blur(12px)", borderTop: "1px solid rgba(255,255,255,0.05)", paddingBottom: 12 }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 22, color: "#7C3AED" }}>home</span>
          <span className="material-symbols-outlined" style={{ fontSize: 22, color: "rgba(255,255,255,0.3)" }}>search</span>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#7C3AED", display: "flex", alignItems: "center", justifyContent: "center", marginTop: -20, border: "4px solid #1C0A2E", boxShadow: "0 4px 20px rgba(124,59,237,0.5)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: "#fff" }}>shopping_bag</span>
          </div>
          <span className="material-symbols-outlined" style={{ fontSize: 22, color: "rgba(255,255,255,0.3)" }}>favorite</span>
          <span className="material-symbols-outlined" style={{ fontSize: 22, color: "rgba(255,255,255,0.3)" }}>person</span>
        </div>
      </div>
    </div>
  );
}

// ─── Glassmorphism Card ───────────────────────────────────────────────────────
function GlassCard({ children, className = "", style = {}, hover = true }) {
  return (
    <div
      className={cn("rounded-2xl transition-all duration-300", className)}
      style={{
        background: "rgba(28, 10, 46, 0.7)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(197, 160, 89, 0.25)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Section Title ────────────────────────────────────────────────────────────
function SectionTitle({ kicker, title, desc, align = "left" }) {
  return (
    <div
      className={cn(
        "max-w-2xl",
        align === "center" && "mx-auto text-center"
      )}
    >
      {kicker && (
        <div
          data-reveal
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4"
          style={{
            border: "1px solid rgba(197,160,89,0.3)",
            background: "rgba(197,160,89,0.05)",
          }}
        >
          <span
            style={{ width: 6, height: 6, borderRadius: "50%", background: "#C5A059", display: "inline-block" }}
          />
          <p
            className="text-xs font-medium tracking-wide"
            style={{ color: "#C5A059" }}
          >
            {kicker}
          </p>
        </div>
      )}
      <h2
        data-reveal
        className="mt-2 text-3xl sm:text-4xl md:text-5xl font-bold leading-tight"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: "#fff",
        }}
      >
        {title}
      </h2>
      {desc && (
        <p
          data-reveal
          className="mt-3 text-base sm:text-lg leading-relaxed"
          style={{ color: "#E8DDD5" }}
        >
          {desc}
        </p>
      )}
    </div>
  );
}

// ─── Consumer Signup Form ─────────────────────────────────────────────────────
function ConsumerSignupForm() {
  const [ageChecked, setAgeChecked] = useState(false);
  const [formData, setFormData] = useState({
    name: "", phone: "", area: "", email: "",
  });

  const inputStyle = {
    width: "100%",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10,
    padding: "12px 16px",
    color: "#fff",
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = "#C5A059";
  };
  const handleBlur = (e) => {
    e.target.style.borderColor = "rgba(255,255,255,0.1)";
  };

  return (
    <section
      id="signup"
      className="relative py-24 px-4 flex items-center justify-center"
      style={{
        background: "linear-gradient(180deg, #171122 0%, #1C0A2E 100%)",
      }}
    >
      <div className="relative w-full max-w-lg z-10">
        <GlassCard
          style={{
            border: "1px solid rgba(197,160,89,0.4)",
            boxShadow: "0 0 60px rgba(124,59,237,0.15)",
            padding: "40px",
          }}
        >
          {/* Social proof above form */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex -space-x-2">
              {[
                "https://lh3.googleusercontent.com/aida-public/AB6AXuD1nAkHgoVHVJjklRDLUVR5mLhswa3vLKfZmjMyxsFztW1KVtFiEH9ZL_c6CW656zwxvGNOL4c1dIfeGw5U5DzLWePyNd67ANKNpkTdRrjXXy7wgZEC4Vh5Pr4ZsZ_d_L4Im_Tx-Pp9UYb6FLxIzyVlBf_guM8yIFFbbPCjA-YZ2ekOjpKKLKgq0spgN9ZYFvFnPcGaL1TTd78uhTwe7WaedjzCM1v-Snt85WNqfj4yZitEtERfSN8X6Ok8dlKyMemMHzNf0kN3SZNu",
                "https://lh3.googleusercontent.com/aida-public/AB6AXuChPJ09Qo6Ij8RpTtATG4Pj0slMkTXeW5j_Sy3BZPDd3vUo2mYgQtoRe2hSggLmJLRUSgtskSo7VO-cRIHhYrrxtklkSnGRRJp4eCFKHtv-XaWZsgWbWcliA29GEiAPbv9NRYF2DMkjBGDxPRZfCP6jGv0082eBPCo6JM01I1RRT7kfZk_yKEUvTfP0RB_zyKK6s1gQtIktcpf-RP3EQ1zQOVrqp81JK-CuoebD4kKR2DpeTnC22Ij433neX_yE28hATPz5QnPqtK-Y",
                "https://lh3.googleusercontent.com/aida-public/AB6AXuAC4viW6ipfa3HTEY7PDHu9Tkp8wNzarRsKOUFSohtWiGOynhKChEPNxR0tnNChOjbcT6UTpeFg7CBogHSUiroOWJF7PDq5xdFIvN2L22XkF_otlZ2z3BS9FA7C-P4Mfj544bS3FJMgJIjAYDwrdcqJFuuC4e-7d5lmer1jW2IVV3rFLzzntiWdBTpaK2Fqxty7kpTa-ldh0CTplSIP9UKAoJ0xgQgoO2CtkVEHRR1tpj5Y-7ZHzTsUjInEMBAMVy27K9scVxaSQBig",
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "2px solid #1C0A2E",
                    objectFit: "cover",
                  }}
                />
              ))}
            </div>
            <p style={{ fontSize: 13, color: "#C5A059" }}>
              Join Mumbai's <strong>exclusive waitlist</strong>
            </p>
          </div>

          <h2
            className="font-bold mb-2"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 32,
              color: "#fff",
            }}
          >
            Get Early Access
          </h2>
          <p style={{ fontSize: 14, color: "#E8DDD5", marginBottom: 24 }}>
            Be among the first in Mumbai. No payment needed.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Name */}
            <div>
              <label style={{ display: "block", fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>
                Full Name
              </label>
              <input
                type="text"
                placeholder="Aditya Roy"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Phone */}
            <div>
              <label style={{ display: "block", fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>
                Phone Number
              </label>
              <div style={{ position: "relative" }}>
                <span style={{
                  position: "absolute", left: 16, top: "50%",
                  transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)",
                  fontSize: 14,
                }}>
                  +91
                </span>
                <input
                  type="tel"
                  placeholder="98765 43210"
                  style={{ ...inputStyle, paddingLeft: 48 }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            {/* Area */}
            <div>
              <label style={{ display: "block", fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>
                Area in Mumbai
              </label>
              <select
                style={{
                  ...inputStyle,
                  cursor: "pointer",
                  backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23C5A059' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: 20,
                  appearance: "none",
                }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
              >
                <option value="" disabled>Choose your neighbourhood</option>
                {MUMBAI_AREAS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            {/* Email optional */}
            <div>
              <label style={{ display: "block", fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 6 }}>
                Email{" "}
                <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 12 }}>(Optional)</span>
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            {/* Age checkbox */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                <input
                  type="checkbox"
                  id="age-confirm"
                  checked={ageChecked}
                  onChange={(e) => setAgeChecked(e.target.checked)}
                  style={{ opacity: 0, position: "absolute", width: 20, height: 20, cursor: "pointer", margin: 0 }}
                />
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    border: `2px solid ${ageChecked ? "#C5A059" : "rgba(255,255,255,0.25)"}`,
                    background: ageChecked ? "#C5A059" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                    cursor: "pointer",
                  }}
                  onClick={() => setAgeChecked(!ageChecked)}
                >
                  {ageChecked && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#0A0709" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>
              <label
                htmlFor="age-confirm"
                style={{ fontSize: 13, color: "#E8DDD5", cursor: "pointer", lineHeight: 1.4 }}
              >
                I confirm I am{" "}
                <strong style={{ color: "#C5A059" }}>25 years or older</strong>
              </label>
            </div>

            {/* Submit */}
            <button
              disabled={!ageChecked}
              style={{
                width: "100%",
                padding: "16px",
                borderRadius: 10,
                background: ageChecked ? "#7C3AED" : "rgba(124,59,237,0.3)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 15,
                border: "none",
                cursor: ageChecked ? "pointer" : "not-allowed",
                boxShadow: ageChecked ? "0 0 20px rgba(124,59,237,0.4)" : "none",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              Join the Waitlist
              <ArrowRight size={18} />
            </button>

            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", textAlign: "center" }}>
              No spam. We'll only notify you on launch day.
            </p>
          </div>
        </GlassCard>
      </div>
    </section>
  );
}

// ─── Driver Form ──────────────────────────────────────────────────────────────
function DriverForm() {
  const [licenseYes, setLicenseYes] = useState(false);

  const inputStyle = {
    width: "100%",
    background: "rgba(49,36,71,0.5)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10,
    padding: "12px 16px",
    color: "#fff",
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  };

  const handleFocus = (e) => { e.target.style.borderColor = "#7C3AED"; };
  const handleBlur = (e) => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; };

  return (
    <GlassCard
      style={{
        border: "1px solid rgba(197,160,89,0.3)",
        padding: 32,
        position: "relative",
      }}
    >
      {/* Glow behind card */}
      <div
        style={{
          position: "absolute",
          inset: -1,
          borderRadius: 16,
          background: "linear-gradient(135deg, rgba(124,59,237,0.3), rgba(139,26,47,0.2))",
          filter: "blur(20px)",
          zIndex: -1,
          opacity: 0.5,
        }}
      />
      <h3
        className="font-bold mb-6"
        style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, color: "#fff" }}
      >
        Join the Fleet
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <input type="text" placeholder="Full Name" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
        <input type="tel" placeholder="+91 98765 43210" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <select
            style={{ ...inputStyle, appearance: "none", cursor: "pointer" }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <option>Bike</option>
            <option>Scooter</option>
            <option>Car</option>
          </select>
          <input type="text" placeholder="Area (e.g. Bandra)" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
        </div>

        {/* License toggle */}
        <div className="flex items-center justify-between" style={{ padding: "8px 0" }}>
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#C5A059" }}>badge</span>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>Valid Driver's License?</span>
          </div>
          <div
            onClick={() => setLicenseYes(!licenseYes)}
            style={{
              width: 48,
              height: 26,
              borderRadius: 13,
              background: licenseYes ? "#C5A059" : "rgba(49,36,71,0.8)",
              border: "1px solid rgba(255,255,255,0.2)",
              cursor: "pointer",
              position: "relative",
              transition: "background 0.25s",
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#fff",
                position: "absolute",
                top: 3,
                left: licenseYes ? 26 : 4,
                transition: "left 0.25s",
              }}
            />
          </div>
        </div>

        <button
          style={{
            width: "100%",
            padding: "16px",
            borderRadius: 10,
            background: "#7C3AED",
            color: "#fff",
            fontWeight: 700,
            fontSize: 15,
            border: "none",
            cursor: "pointer",
            boxShadow: "0 0 20px rgba(124,59,237,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            transition: "all 0.2s",
          }}
        >
          Submit Application
          <ArrowRight size={18} />
        </button>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", textAlign: "center" }}>
          We'll reach out within 24 hours to verify your details.
        </p>
      </div>
    </GlassCard>
  );
}

// ─── Mobile Nav (Popover Hamburger) ─────────────────────────────────────────
function MobileNav({ user, handleLoginSuccess, handleLogout }) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="md:hidden relative flex items-center justify-center"
          style={{ width: 36, height: 36, background: "none", border: "none", cursor: "pointer", padding: 6 }}
          aria-label="Toggle menu"
        >
          <div style={{ position: "relative", width: 18, height: 18 }}>
            <span
              style={{
                position: "absolute", left: 0, display: "block",
                height: 2, width: 18, borderRadius: 2,
                background: open ? "#fff" : "rgba(232,221,213,0.8)",
                transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
                top: open ? 7 : 4,
                transform: open ? "rotate(-45deg)" : "none",
              }}
            />
            <span
              style={{
                position: "absolute", left: 0, display: "block",
                height: 2, width: 18, borderRadius: 2,
                background: open ? "#fff" : "rgba(232,221,213,0.8)",
                transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
                top: open ? 7 : 12,
                transform: open ? "rotate(45deg)" : "none",
              }}
            />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="rounded-none border-none p-0 shadow-2xl"
        style={{
          width: "100vw",
          maxWidth: "100vw",
          background: "rgba(10,7,9,0.97)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex flex-col gap-8 px-6 py-8">
          {/* Nav links */}
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => { scrollToId(l.id); setOpen(false); }}
                className="text-left py-2 font-medium transition-colors"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 28,
                  color: "#E8DDD5",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#C5A059")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#E8DDD5")}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div style={{ height: 1, background: "rgba(255,255,255,0.08)" }} />

          {/* CTAs */}
          {!user ? (
            <div className="flex flex-col gap-3">
              <WaitlistDialog defaultInterest="user" source="mobile_primary" onLoginSuccess={handleLoginSuccess}>
                <Button
                  className="h-12 w-full rounded-xl font-bold"
                  onClick={() => setOpen(false)}
                  style={{ background: "#7C3AED", color: "#fff" }}
                >
                  Get Early Access
                </Button>
              </WaitlistDialog>
              <WaitlistDialog defaultInterest="driver" source="mobile_driver" onLoginSuccess={handleLoginSuccess}>
                <Button
                  className="h-12 w-full rounded-xl"
                  onClick={() => setOpen(false)}
                  style={{ border: "1px solid rgba(197,160,89,0.4)", color: "#C5A059", background: "transparent" }}
                >
                  Deliver With Us
                </Button>
              </WaitlistDialog>
            </div>
          ) : (
            <div>
              <UserProfile user={user} onLogout={() => { handleLogout(); setOpen(false); }} />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Main Landing Component ───────────────────────────────────────────────────
export default function LiquoRitLanding() {
  usePremiumFonts();
  const solidNav = useScrollTopBg();
  const [openFromNav, setOpenFromNav] = useState(null);
  const closeTimerRef = useRef(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showAdminButton, setShowAdminButton] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [footerEmail, setFooterEmail] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mode") === "admin") {
      setShowAdminButton(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("liquorit_user");
    if (stored) {
      try { setUser(JSON.parse(stored)); } catch {}
    }
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    localStorage.setItem("liquorit_user", JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("liquorit_user");
    toast.success("Signed out successfully");
  };

  useRevealOnScroll();
  const parallax = useParallax();

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = Math.max(1, doc.scrollHeight - doc.clientHeight);
      setScrollProgress(Math.max(0, Math.min(1, window.scrollY / max)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!openFromNav) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpenFromNav(null), 500);
    return () => clearTimeout(closeTimerRef.current);
  }, [openFromNav]);

  // Inject keyframes
  useEffect(() => {
    if (document.getElementById("bottlerun-keyframes")) return;
    const style = document.createElement("style");
    style.id = "bottlerun-keyframes";
    style.innerHTML = `
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-18px); }
      }
      @keyframes marquee {
        0% { transform: translateX(0%); }
        100% { transform: translateX(-50%); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 0.7; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.3); }
      }
      @keyframes fadeUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // ── Feature icon color ──
  const featureColor = (color) => {
    if (color === "violet") return "#7C3AED";
    if (color === "bordeaux") return "#8B1A2F";
    return "#C5A059";
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden w-full relative"
      style={{ background: "#0A0709", color: "#E8DDD5" }}
    >
      {/* Grain overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E\")",
          mixBlendMode: "overlay",
          opacity: 0.35,
        }}
      />

      {/* Scroll progress bar */}
      <div
        className="fixed top-0 left-0 right-0 z-50 h-0.5"
        style={{ background: "rgba(197,160,89,0.1)" }}
        aria-hidden
      >
        <div
          style={{
            height: "100%",
            transformOrigin: "left",
            transform: `scaleX(${scrollProgress})`,
            background: "#C5A059",
            boxShadow: "0 0 12px rgba(197,160,89,0.5)",
          }}
        />
      </div>

      {/* ── NAVBAR ─────────────────────────────────────────────────────────── */}
      <header
        className="fixed inset-x-0 top-0 z-40 transition-all duration-300"
        style={{
          background: solidNav ? "rgba(10,7,9,0.92)" : "transparent",
          backdropFilter: solidNav ? "blur(20px)" : "none",
          borderBottom: solidNav ? "1px solid rgba(255,255,255,0.06)" : "none",
        }}
      >
        <div
          className="mx-auto flex max-w-7xl items-center justify-between px-6"
          style={{ height: 72 }}
        >
          {/* Logo */}
          <button
            onClick={() => scrollToId("top")}
            className="flex items-center gap-2 group"
          >
            <span
              className="material-symbols-outlined transition-transform duration-300 group-hover:rotate-12"
              style={{ fontSize: 28, color: "#C5A059" }}
            >
              liquor
            </span>
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 22,
                fontWeight: 600,
                color: "#C5A059",
                letterSpacing: "-0.02em",
              }}
            >
              Liquorit
            </span>
          </button>

          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollToId(l.id)}
                className="text-sm font-medium transition-colors duration-200"
                style={{ color: "#E8DDD5" }}
                onMouseEnter={(e) => (e.target.style.color = "#C5A059")}
                onMouseLeave={(e) => (e.target.style.color = "#E8DDD5")}
              >
                {l.label}
              </button>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden md:flex items-center gap-3">
            {!user ? (
              <>
                <WaitlistDialog defaultInterest="both" source="nav_primary" onLoginSuccess={handleLoginSuccess}>
                  <button
                    className="text-sm font-medium px-5 py-2 rounded-lg transition-all duration-200"
                    style={{
                      border: "1px solid rgba(197,160,89,0.4)",
                      color: "#C5A059",
                      background: "transparent",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(197,160,89,0.08)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  >
                    Sign Up Free
                  </button>
                </WaitlistDialog>
                <WaitlistDialog defaultInterest="driver" source="nav_driver" onLoginSuccess={handleLoginSuccess}>
                  <button
                    className="text-sm font-bold px-5 py-2 rounded-lg transition-all duration-200"
                    style={{
                      background: "#7C3AED",
                      color: "#fff",
                      boxShadow: "0 0 16px rgba(124,59,237,0.35)",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#6D28D9"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#7C3AED"; }}
                  >
                    Deliver With Us
                  </button>
                </WaitlistDialog>
              </>
            ) : (
              <UserProfile user={user} onLogout={handleLogout} />
            )}
          </div>

          {/* Mobile menu */}
          <div className="md:hidden">
            <MobileNav user={user} handleLoginSuccess={handleLoginSuccess} handleLogout={handleLogout} />
          </div>
        </div>
      </header>

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <main id="top" className="relative">
        {/* Outer padding wrapper */}
        <div className="relative" style={{ paddingTop: 72, paddingBottom: 48 }}>
          {/* ── HERO CARD ── */}
          <div
            className="relative overflow-hidden"
            style={{
              margin: "0 16px",
              borderRadius: 48,
              border: "1px solid rgba(197,160,89,0.18)",
              minHeight: "calc(100dvh - 160px)",
              boxShadow: "0 32px 80px rgba(0,0,0,0.5)",
            }}
          >
            {/* Background image */}
            <img
              src="/images/hero.webp"
              alt=""
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ zIndex: 0 }}
            />
            {/* Dark gradient overlay — left text readable, right reveals image */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                zIndex: 1,
                background:
                  "linear-gradient(110deg, rgba(10,7,9,0.96) 0%, rgba(10,7,9,0.82) 42%, rgba(28,10,46,0.72) 70%, rgba(28,10,46,0.55) 100%)",
              }}
            />
            {/* Bottom fade for marquee blend */}
            <div
              className="absolute bottom-0 left-0 right-0 pointer-events-none"
              style={{
                zIndex: 2,
                height: 140,
                background: "linear-gradient(to top, rgba(10,7,9,1) 0%, transparent 100%)",
              }}
            />

            {/* Content grid */}
            <div
              className="relative z-10 mx-auto grid max-w-7xl w-full items-center gap-12 px-8 md:grid-cols-2"
              style={{ paddingTop: 80, paddingBottom: 60 }}
            >
              {/* Left text */}
              <div className="flex flex-col gap-8">
                {/* Eyebrow */}
                <div data-reveal>
                  <span
                    className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase rounded-full px-3 py-1"
                    style={{
                      border: "1px solid rgba(197,160,89,0.3)",
                      background: "rgba(197,160,89,0.05)",
                      color: "#C5A059",
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#C5A059", display: "inline-block", animation: "pulse 2s ease-in-out infinite" }} />
                    Now in Mumbai
                  </span>
                </div>

                {/* Headline */}
                <div data-reveal>
                  <h1
                    className="leading-tight"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: "clamp(3rem, 6vw, 5rem)",
                      fontWeight: 700,
                      color: "#fff",
                      lineHeight: 1.05,
                    }}
                  >
                    Your favourite
                    <br />
                    <em style={{ color: "#C5A059", fontStyle: "italic" }}>bottle.</em>
                    <br />
                    Delivered.
                  </h1>
                </div>

                {/* Sub */}
                <p
                  data-reveal
                  style={{ fontSize: 18, color: "#E8DDD5", maxWidth: 420, lineHeight: 1.65, fontWeight: 300 }}
                >
                  From licensed stores across Mumbai, tracked live, at your door.
                </p>

                {/* Social proof */}
                <div data-reveal className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    {[
                      "https://lh3.googleusercontent.com/aida-public/AB6AXuD1nAkHgoVHVJjklRDLUVR5mLhswa3vLKfZmjMyxsFztW1KVtFiEH9ZL_c6CW656zwxvGNOL4c1dIfeGw5U5DzLWePyNd67ANKNpkTdRrjXXy7wgZEC4Vh5Pr4ZsZ_d_L4Im_Tx-Pp9UYb6FLxIzyVlBf_guM8yIFFbbPCjA-YZ2ekOjpKKLKgq0spgN9ZYFvFnPcGaL1TTd78uhTwe7WaedjzCM1v-Snt85WNqfj4yZitEtERfSN8X6Ok8dlKyMemMHzNf0kN3SZNu",
                      "https://lh3.googleusercontent.com/aida-public/AB6AXuChPJ09Qo6Ij8RpTtATG4Pj0slMkTXeW5j_Sy3BZPDd3vUo2mYgQtoRe2hSggLmJLRUSgtskSo7VO-cRIHhYrrxtklkSnGRRJp4eCFKHtv-XaWZsgWbWcliA29GEiAPbv9NRYF2DMkjBGDxPRZfCP6jGv0082eBPCo6JM01I1RRT7kfZk_yKEUvTfP0RB_zyKK6s1gQtIktcpf-RP3EQ1zQOVrqp81JK-CuoebD4kKR2DpeTnC22Ij433neX_yE28hATPz5QnPqtK-Y",
                      "https://lh3.googleusercontent.com/aida-public/AB6AXuAC4viW6ipfa3HTEY7PDHu9Tkp8wNzarRsKOUFSohtWiGOynhKChEPNxR0tnNChOjbcT6UTpeFg7CBogHSUiroOWJF7PDq5xdFIvN2L22XkF_otlZ2z3BS9FA7C-P4Mfj544bS3FJMgJIjAYDwrdcqJFuuC4e-7d5lmer1jW2IVV3rFLzzntiWdBTpaK2Fqxty7kpTa-ldh0CTplSIP9UKAoJ0xgQgoO2CtkVEHRR1tpj5Y-7ZHzTsUjInEMBAMVy27K9scVxaSQBig",
                    ].map((s, i) => (
                      <img key={i} src={s} alt="" style={{ width: 36, height: 36, borderRadius: "50%", border: "2px solid rgba(28,10,46,0.9)", objectFit: "cover" }} />
                    ))}
                  </div>
                  <p style={{ fontSize: 14, color: "#C5A059", fontWeight: 500 }}>
                    Join Mumbai's <strong>exclusive waitlist</strong>
                  </p>
                </div>

                {/* CTAs */}
                <div data-reveal className="flex flex-col sm:flex-row gap-4">
                  <WaitlistDialog defaultInterest="both" source="hero_primary" onLoginSuccess={handleLoginSuccess}>
                    <button
                      className="flex items-center justify-center gap-2 font-bold text-base transition-all duration-300"
                      style={{ height: 54, padding: "0 32px", background: "#7C3AED", color: "#fff", boxShadow: "0 0 24px rgba(124,59,237,0.45)", border: "none", cursor: "pointer", borderRadius: 12 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#6D28D9"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 0 36px rgba(124,59,237,0.6)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#7C3AED"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 0 24px rgba(124,59,237,0.45)"; }}
                    >
                      Get Early Access <ArrowRight size={18} />
                    </button>
                  </WaitlistDialog>
                  <WaitlistDialog defaultInterest="driver" source="hero_secondary" onLoginSuccess={handleLoginSuccess}>
                    <button
                      className="flex items-center justify-center font-semibold text-base transition-all duration-200"
                      style={{ height: 54, padding: "0 32px", border: "1px solid rgba(139,26,47,0.5)", color: "#E8DDD5", background: "transparent", cursor: "pointer", borderRadius: 12 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(139,26,47,0.1)"; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#E8DDD5"; }}
                    >
                      Become a Partner
                    </button>
                  </WaitlistDialog>
                </div>

                {/* Trust badges */}
                <div data-reveal className="flex flex-wrap gap-5" style={{ opacity: 0.7 }}>
                  {[
                    { icon: "verified_user", label: "Licensed Stores" },
                    { icon: "18_up_rating", label: "21+ Verified" },
                    { icon: "location_on", label: "Mumbai Only" },
                  ].map((b) => (
                    <div key={b.label} className="flex items-center gap-2">
                      <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#C5A059" }}>{b.icon}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#E8DDD5", textTransform: "uppercase" }}>{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Phone mockup */}
              <div data-reveal ref={parallax(0.1)}>
                <PhoneMock />
              </div>
            </div>
          </div>
        </div>

        {/* ── TRUST MARQUEE STRIP ──────────────────────────────────────────── */}
        <section
          style={{
            background: "#110D0A",
            borderTop: "3px solid #8B1A2F",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            padding: "20px 0",
            overflow: "hidden",
          }}
        >
          <p
            style={{
              textAlign: "center",
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              fontSize: 14,
              color: "rgba(197,160,89,0.7)",
              marginBottom: 14,
              paddingLeft: 24,
            }}
          >
            Partnering with licensed stores across Mumbai
          </p>
          <div className="relative w-full flex overflow-hidden">
            <div
              style={{
                display: "flex",
                gap: 64,
                animation: "marquee 28s linear infinite",
                whiteSpace: "nowrap",
              }}
            >
              {STORE_PARTNERS.map((s, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.15em",
                    color: "rgba(232,221,213,0.35)",
                    cursor: "default",
                    transition: "opacity 0.2s",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(232,221,213,0.9)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(232,221,213,0.35)")}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* ── HOW IT WORKS ───────────────────────────────────────────────────── */}
      <section
        id="how"
        className="relative py-24 px-6"
        style={{ background: "#171122", overflow: "hidden" }}
      >
        {/* Purple top blur */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "60%",
            maxWidth: 600,
            height: 200,
            background: "rgba(124,59,237,0.08)",
            filter: "blur(80px)",
            borderRadius: "50%",
          }}
        />
        <div className="max-w-7xl mx-auto relative z-10">
          <SectionTitle
            kicker="How Liquorit Works"
            title="Three steps between you and your drink."
            align="center"
          />
          <p
            className="text-center mt-3 mb-16"
            style={{ color: "#C5A059", fontSize: 18, fontWeight: 500 }}
          >
            No membership. No minimum order.
          </p>

          {/* Cards */}
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">

            {HOW_IT_WORKS.map((step, idx) => (
              <div
                key={step.title}
                data-reveal
                className="relative z-10 flex flex-col items-center text-center p-8 rounded-2xl transition-all duration-300 cursor-default"
                style={{
                  background: "rgba(28,10,46,0.7)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  border: "1px solid rgba(197,160,89,0.25)",
                  animationDelay: `${idx * 100}ms`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-6px)";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(139,26,47,0.25)";
                  e.currentTarget.style.borderColor = "rgba(197,160,89,0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "rgba(197,160,89,0.25)";
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 14,
                    left: 14,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: "1px solid rgba(197,160,89,0.5)",
                    background: "rgba(197,160,89,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#C5A059",
                  }}
                >
                  {idx + 1}
                </div>
                <div
                  className="flex items-center justify-center mb-6"
                  style={{
                    width: 76,
                    height: 76,
                    borderRadius: "50%",
                    background: "#110D0A",
                    border: "1px solid rgba(197,160,89,0.4)",
                    boxShadow: "0 0 20px rgba(197,160,89,0.08)",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 34, color: "#C5A059" }}
                  >
                    {step.icon}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: "#fff" }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: 14, color: "#E8DDD5", lineHeight: 1.65 }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>

          {/* CTA #2 */}
          <div className="flex flex-col items-center mt-16 gap-3">
            <p style={{ fontSize: 13, color: "#C5A059" }}>Ready to try it?</p>
            <WaitlistDialog defaultInterest="both" source="howitworks_cta" onLoginSuccess={handleLoginSuccess}>
              <button
                className="flex items-center gap-2 font-bold rounded-lg transition-all duration-300"
                style={{
                  padding: "14px 36px",
                  background: "#7C3AED",
                  color: "#fff",
                  boxShadow: "0 0 24px rgba(124,59,237,0.4)",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 15,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                Get Early Access <ArrowRight size={16} />
              </button>
            </WaitlistDialog>
          </div>
        </div>
      </section>

      {/* ── FEATURES ───────────────────────────────────────────────────────── */}
      <section
        id="features"
        className="relative py-24 px-6"
        style={{ background: "#0F0B08" }}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionTitle
              kicker="Features"
              title="Built different."
            />
            <p
              style={{ fontSize: 20, color: "#C5A059", fontWeight: 500, marginTop: 12, marginBottom: 16 }}
            >
              Everything the other apps missed.
            </p>
            <p style={{ fontSize: 15, color: "#E8DDD5", lineHeight: 1.7, maxWidth: 420 }}>
              We aren't just a delivery service — we're a premium concierge for your finest moments,
              designed specifically for the discerning Mumbai consumer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FEATURES.map((f, idx) => (
              <div
                key={f.title}
                data-reveal
                className="flex flex-col gap-4 p-6 rounded-xl transition-all duration-300 cursor-default"
                style={{
                  background: "#110D0A",
                  border: `1px solid rgba(139,26,47,0.3)`,
                  animationDelay: `${idx * 80}ms`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(197,160,89,0.35)";
                  e.currentTarget.style.transform = "translateY(-3px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(139,26,47,0.3)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 36, color: featureColor(f.color) }}
                >
                  {f.icon}
                </span>
                <div>
                  <h4 style={{ fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
                    {f.title}
                  </h4>
                  <p style={{ fontSize: 13, color: "#E8DDD5", lineHeight: 1.6 }}>
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DRIVER SECTION ──────────────────────────────────────────────────── */}
      <section
        id="drivers"
        className="relative py-24 px-6"
        style={{
          background: "#110D0A",
          borderTop: "1px solid rgba(139,26,47,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Subtle violet right glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            right: 0,
            top: 0,
            bottom: 0,
            width: "50%",
            background: "linear-gradient(to left, rgba(124,59,237,0.04), transparent)",
          }}
        />
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div className="flex flex-col gap-6">
            <div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "#7C3AED",
                  display: "block",
                  marginBottom: 12,
                }}
              >
                For Delivery Partners
              </span>
              <h2
                className="font-bold"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "clamp(2.2rem, 4vw, 3.5rem)",
                  color: "#fff",
                  lineHeight: 1.1,
                }}
              >
                Earn on your schedule.
              </h2>
              <p style={{ color: "#E8DDD5", fontSize: 16, marginTop: 16, maxWidth: 420, lineHeight: 1.65 }}>
                Mumbai's liquor delivery market is just getting started. Get in early and
                grow with a platform built to respect your time and effort.
              </p>
            </div>

            <div className="flex flex-col gap-7 mt-4">
              {DRIVER_PERKS.map((p) => (
                <div key={p.title} className="flex gap-5 items-start">
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      background: "rgba(124,59,237,0.1)",
                      border: "1px solid rgba(124,59,237,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 22, color: "#7C3AED" }}>
                      {p.icon}
                    </span>
                  </div>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 600, color: "#fff", marginBottom: 4 }}>
                      {p.title}
                    </h3>
                    <p style={{ fontSize: 13, color: "#E8DDD5" }}>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <p style={{ fontSize: 13, color: "#C5A059", marginTop: 8 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 6 }}>
                shield
              </span>
              All partners are background-verified before going live.
            </p>
          </div>

          {/* Right: Apply via WaitlistDialog */}
          <div
            className="relative rounded-2xl p-8"
            style={{
              background: "rgba(28,10,46,0.7)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              border: "1px solid rgba(197,160,89,0.3)",
            }}
          >
            {/* Glow */}
            <div style={{position:"absolute",inset:-1,borderRadius:16,background:"linear-gradient(135deg,rgba(124,59,237,0.25),rgba(139,26,47,0.15))",filter:"blur(20px)",zIndex:-1,opacity:0.5}} />

            <h3 className="font-bold mb-2" style={{fontFamily:"'Inter',sans-serif",fontSize:20,color:"#fff"}}>
              Join the Fleet
            </h3>
            <p style={{fontSize:14,color:"#E8DDD5",marginBottom:24,lineHeight:1.6}}>
              Apply in 60 seconds. We verify your details and get back within 24 hours.
            </p>

            {/* Social proof avatars */}
            <div className="flex items-center gap-3 mb-8">
              <div className="flex -space-x-2">
                {[
                  "https://lh3.googleusercontent.com/aida-public/AB6AXuD1nAkHgoVHVJjklRDLUVR5mLhswa3vLKfZmjMyxsFztW1KVtFiEH9ZL_c6CW656zwxvGNOL4c1dIfeGw5U5DzLWePyNd67ANKNpkTdRrjXXy7wgZEC4Vh5Pr4ZsZ_d_L4Im_Tx-Pp9UYb6FLxIzyVlBf_guM8yIFFbbPCjA-YZ2ekOjpKKLKgq0spgN9ZYFvFnPcGaL1TTd78uhTwe7WaedjzCM1v-Snt85WNqfj4yZitEtERfSN8X6Ok8dlKyMemMHzNf0kN3SZNu",
                  "https://lh3.googleusercontent.com/aida-public/AB6AXuChPJ09Qo6Ij8RpTtATG4Pj0slMkTXeW5j_Sy3BZPDd3vUo2mYgQtoRe2hSggLmJLRUSgtskSo7VO-cRIHhYrrxtklkSnGRRJp4eCFKHtv-XaWZsgWbWcliA29GEiAPbv9NRYF2DMkjBGDxPRZfCP6jGv0082eBPCo6JM01I1RRT7kfZk_yKEUvTfP0RB_zyKK6s1gQtIktcpf-RP3EQ1zQOVrqp81JK-CuoebD4kKR2DpeTnC22Ij433neX_yE28hATPz5QnPqtK-Y",
                  "https://lh3.googleusercontent.com/aida-public/AB6AXuAC4viW6ipfa3HTEY7PDHu9Tkp8wNzarRsKOUFSohtWiGOynhKChEPNxR0tnNChOjbcT6UTpeFg7CBogHSUiroOWJF7PDq5xdFIvN2L22XkF_otlZ2z3BS9FA7C-P4Mfj544bS3FJMgJIjAYDwrdcqJFuuC4e-7d5lmer1jW2IVV3rFLzzntiWdBTpaK2Fqxty7kpTa-ldh0CTplSIP9UKAoJ0xgQgoO2CtkVEHRR1tpj5Y-7ZHzTsUjInEMBAMVy27K9scVxaSQBig",
                ].map((src, i) => (
                  <img key={i} src={src} alt="" style={{width:32,height:32,borderRadius:"50%",border:"2px solid #1C0A2E",objectFit:"cover"}} />
                ))}
              </div>
              <p style={{fontSize:13,color:"#C5A059"}}>
                Join Mumbai's <strong>exclusive waitlist</strong>
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <WaitlistDialog defaultInterest="driver" source="drivers_section" onLoginSuccess={handleLoginSuccess}>
                <button
                  className="w-full flex items-center justify-center gap-2 rounded-xl font-bold transition-all duration-200"
                  style={{padding:"16px",background:"#7C3AED",color:"#fff",border:"none",cursor:"pointer",boxShadow:"0 0 20px rgba(124,59,237,0.4)",fontSize:15}}
                  onMouseEnter={(e)=>{e.currentTarget.style.background="#6D28D9";e.currentTarget.style.transform="translateY(-1px)";}}
                  onMouseLeave={(e)=>{e.currentTarget.style.background="#7C3AED";e.currentTarget.style.transform="translateY(0)";}}
                >
                  Apply to Drive <ArrowRight size={18}/>
                </button>
              </WaitlistDialog>
              <p style={{fontSize:12,color:"rgba(255,255,255,0.35)",textAlign:"center"}}>
                We'll reach out within 24 hours to verify your details.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── LAUNCH CTA SECTION ─────────────────────────────────────────── */}
      <section
        className="relative py-20 px-6 overflow-hidden"
        style={{ background: "#0A0709", borderTop: "1px solid rgba(255,255,255,0.04)" }}
      >
        {/* Ambient glows */}
        <div className="absolute pointer-events-none" style={{ left: "10%", top: "50%", transform: "translateY(-50%)", width: 400, height: 400, background: "radial-gradient(circle, rgba(124,59,237,0.10) 0%, transparent 70%)", filter: "blur(60px)" }} />
        <div className="absolute pointer-events-none" style={{ right: "10%", top: "50%", transform: "translateY(-50%)", width: 300, height: 300, background: "radial-gradient(circle, rgba(197,160,89,0.08) 0%, transparent 70%)", filter: "blur(60px)" }} />

        <div className="relative z-10 max-w-7xl mx-auto">
          

          {/* Card — full-width launch CTA */}
          <div
            data-reveal
            className="relative overflow-hidden rounded-[32px] flex flex-col md:flex-row items-center justify-between gap-10 px-10 py-12"
            style={{
              background: "linear-gradient(135deg, rgba(28,10,46,0.95) 0%, rgba(17,13,10,0.95) 100%)",
              border: "1px solid rgba(197,160,89,0.2)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
            }}
          >
            {/* Card ambient glow */}
            <div className="absolute pointer-events-none" style={{ right: -60, top: -60, width: 320, height: 320, background: "radial-gradient(circle, rgba(124,59,237,0.15) 0%, transparent 70%)", filter: "blur(40px)" }} />
            <div className="absolute pointer-events-none" style={{ left: -40, bottom: -40, width: 240, height: 240, background: "radial-gradient(circle, rgba(197,160,89,0.10) 0%, transparent 70%)", filter: "blur(40px)" }} />

            {/* Left: text */}
            <div className="relative z-10 flex flex-col gap-4 max-w-lg">
              <div className="flex items-center gap-2">
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#C5A059", display: "inline-block", animation: "pulse 2s ease-in-out infinite" }} />
                <span
                  className="text-xs font-bold tracking-widest uppercase"
                  style={{ color: "#C5A059" }}
                >
                  Mumbai Launch
                </span>
              </div>
              <h2
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1.15,
                }}
              >
                Liquorit is launching in Mumbai first.
                <em style={{ color: "#C5A059", fontStyle: "italic", display: "block" }}>
                  Get in before the city does.
                </em>
              </h2>
              {/* Trust badges row */}
              <div className="flex flex-wrap gap-4 mt-2">
                {[
                  { icon: "verified", text: "100% Licensed Store Partners" },
                  { icon: "location_on", text: "Mumbai First" },
                  { icon: "bolt", text: "Under 30 Min Delivery" },
                ].map((b) => (
                  <div key={b.text} className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined" style={{ fontSize: 15, color: "#C5A059" }}>{b.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "rgba(232,221,213,0.65)" }}>{b.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: CTA */}
            <div className="relative z-10 flex flex-col items-center gap-4 flex-shrink-0">
              <WaitlistDialog defaultInterest="both" source="launch_cta" onLoginSuccess={handleLoginSuccess}>
                <button
                  className="flex items-center gap-2 font-bold rounded-2xl transition-all duration-300"
                  style={{
                    padding: "16px 40px",
                    background: "#7C3AED",
                    color: "#fff",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 16,
                    boxShadow: "0 0 30px rgba(124,59,237,0.45)",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#6D28D9"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 0 44px rgba(124,59,237,0.6)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#7C3AED"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 0 30px rgba(124,59,237,0.45)"; }}
                >
                  Get Early Access <ArrowRight size={18} />
                </button>
              </WaitlistDialog>
              <p style={{ fontSize: 12, color: "rgba(232,221,213,0.35)", textAlign: "center" }}>
                No payment needed. First 500 users get priority access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section
        id="faq"
        className="py-24 px-6"
        style={{ background: "#110D0A" }}
      >
        <div className="max-w-3xl mx-auto">
          <SectionTitle kicker="FAQ" title="Quick answers." align="center" />
          <div className="mt-12 flex flex-col gap-3">
            {FAQS.map((f, idx) => (
              <div
                key={f.q}
                data-reveal
                className="rounded-xl overflow-hidden transition-all duration-300"
                style={{
                  background: "rgba(28,10,46,0.5)",
                  border: `1px solid ${openFaq === idx ? "rgba(197,160,89,0.4)" : "rgba(255,255,255,0.06)"}`,
                  animationDelay: `${idx * 80}ms`,
                }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span style={{ fontSize: 15, fontWeight: 600, color: openFaq === idx ? "#C5A059" : "#fff" }}>
                    {f.q}
                  </span>
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: 20,
                      color: "#C5A059",
                      transform: openFaq === idx ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.3s",
                      flexShrink: 0,
                    }}
                  >
                    expand_more
                  </span>
                </button>
                {openFaq === idx && (
                  <div style={{ padding: "0 24px 20px", animation: "fadeUp 0.2s ease" }}>
                    <p style={{ fontSize: 14, color: "#E8DDD5", lineHeight: 1.7 }}>{f.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE STRIP ────────────────────────────────────────────────── */}
      <section
        style={{
          background: "rgba(139,26,47,0.12)",
          borderTop: "1px solid rgba(139,26,47,0.4)",
          borderBottom: "1px solid rgba(139,26,47,0.4)",
          padding: "14px 24px",
        }}
      >
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center md:justify-between items-center gap-4">
          {[
            { icon: "verified", text: "Govt. Licensed Retailer Partners Only" },
            { icon: "18_up_rating", text: "21+ Age Limit — Strictly Enforced at Delivery" },
            { icon: "location_on", text: "Mumbai Municipal Limits Only" },
          ].map((c) => (
            <div key={c.text} className="flex items-center gap-2">
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "#C5A059" }}>
                {c.icon}
              </span>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#E8DDD5", letterSpacing: "0.02em" }}>
                {c.text}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer style={{ background: "#0A0709", borderTop: "1px solid rgba(197,160,89,0.25)" }}>
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-6 flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined" style={{ fontSize: 24, color: "#C5A059" }}>liquor</span>
                <span
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    fontWeight: 600,
                    color: "#C5A059",
                  }}
                >
                  Liquorit
                </span>
              </div>
              <p style={{ fontSize: 14, color: "rgba(232,221,213,0.6)", lineHeight: 1.65, maxWidth: 280 }}>
                {BRAND.tagline}
              </p>
              <p style={{ fontSize: 13, color: "rgba(232,221,213,0.4)" }}>{BRAND.email}</p>
              {showAdminButton && (
                <button
                  onClick={() => setAdminOpen(true)}
                  style={{ fontSize: 11, color: "rgba(255,255,255,0.07)", background: "none", border: "none", cursor: "pointer", textAlign: "left", marginTop: 8, display: "flex", alignItems: "center", gap: 4 }}
                >
                  <Lock size={9} />
                  Admin
                </button>
              )}
            </div>



            {/* Newsletter — opens WaitlistDialog pre-filled with email */}
            <div className="md:col-span-6 flex flex-col gap-4">
              <h4 style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>Stay in the loop</h4>
              <p style={{ fontSize: 12, color: "rgba(232,221,213,0.5)" }}>
                Get exclusive launch offers and updates.
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Your email"
                  value={footerEmail}
                  onChange={(e) => setFooterEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && footerEmail.trim()) {
                      document.getElementById("footer-join-btn")?.click();
                    }
                  }}
                  style={{
                    flex: 1,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    color: "#fff",
                    fontSize: 13,
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "rgba(197,160,89,0.5)")}
                  onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                />
                <WaitlistDialog
                  defaultInterest="user"
                  source="footer_newsletter"
                  defaultEmail={footerEmail}
                  onLoginSuccess={handleLoginSuccess}
                >
                  <button
                    id="footer-join-btn"
                    style={{
                      background: footerEmail.trim() ? "#C5A059" : "rgba(197,160,89,0.3)",
                      color: footerEmail.trim() ? "#0A0709" : "rgba(255,255,255,0.3)",
                      fontWeight: 700,
                      fontSize: 13,
                      padding: "10px 16px",
                      borderRadius: 8,
                      border: "none",
                      cursor: footerEmail.trim() ? "pointer" : "default",
                      transition: "all 0.2s",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Join
                  </button>
                </WaitlistDialog>
              </div>
              <p style={{ fontSize: 11, color: "rgba(232,221,213,0.3)" }}>
                Tapping Join opens our signup — your email will be pre-filled.
              </p>
              {/* Socials */}
              <div className="flex gap-4 mt-1">
                {[
                  {
                    label: "Instagram",
                    svg: (
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                        <path clipRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" fillRule="evenodd" />
                      </svg>
                    ),
                  },
                  {
                    label: "X",
                    svg: (
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    ),
                  },
                ].map((s) => (
                  <a
                    key={s.label}
                    href="#"
                    style={{ color: "rgba(232,221,213,0.4)", transition: "color 0.2s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(232,221,213,0.4)")}
                  >
                    <span className="sr-only">{s.label}</span>
                    {s.svg}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div
            className="flex flex-col md:flex-row justify-between items-center gap-3 pt-8"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          >
            <p style={{ fontSize: 12, color: "rgba(232,221,213,0.35)" }}>
              © 2026 Liquorit. All rights reserved.
            </p>
            <div className="flex items-center gap-2" style={{ color: "rgba(232,221,213,0.3)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>local_bar</span>
              <span style={{ fontSize: 12 }}>Drink Responsibly</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Dashboard */}
      <AdminDashboard open={adminOpen} setOpen={setAdminOpen} />

      {openFromNav && (
        <WaitlistDialog
          defaultInterest={openFromNav}
          source="nav_proxy"
          onLoginSuccess={handleLoginSuccess}
        >
          <span className="hidden" />
        </WaitlistDialog>
      )}
    </div>
  );
}